/**
 * iPhones — la lógica pura de la sección, sin React.
 *
 * LO QUE ERA UNA GRILLA Y AHORA ES UNA LISTA
 * ------------------------------------------
 * Los iPhone sellados son el producto principal del negocio y durante un
 * tiempo tuvieron vista propia: una tarjeta por modelo, una fila por
 * capacidad. Se veía lindo y contestaba UNA pregunta ("¿el 17 Pro? ¿en qué
 * memoria?"), pero pagaba caro: no tenía buscador, no tenía filtros, no
 * mostraba el costo ni el stock, no se podía elegir la densidad y no se
 * parecía en nada a las otras seis solapas de la misma pantalla. Aprender a
 * usar Catalogo era aprender tres pantallas.
 *
 * Hoy los iPhone se listan en la MISMA tabla que los accesorios y los
 * perfumes. Lo único que queda de la grilla es el ORDEN —los modelos nuevos
 * arriba, las capacidades de menor a mayor— porque ese sí es el orden en que
 * el mostrador los recita.
 *
 * Las unidades usadas NO entran: tienen su propia sección, con su ficha por
 * equipo. Tampoco los "(USADO)" genéricos de la lista de precios: son
 * referencia, no lineup.
 */
import { aNumero, type Producto } from "@/lib/tipos";

/**
 * El nombre dice que es un iPhone.
 *
 * La marca se lee del NOMBRE y no de la columna `marca`, que en los equipos
 * viene vacía: el nombre lo arma la ficha (0075) y siempre empieza con el
 * modelo, "IPHONE 15 PRO 128 GB" o "SAMSUNG S22 128GB". Es la misma regla que
 * usa la vista `v_stock_valorizado`, para que la consulta y la pantalla no
 * puedan contestar distinto.
 */
export function esIphonePorNombre(nombre: string): boolean {
  return /^IPHONE\b/i.test(nombre);
}

/** Un iPhone sellado del catálogo: empieza con IPHONE y no es (USADO). */
export function esIphoneSellado(p: Producto): boolean {
  return !p.es_usado && esIphonePorNombre(p.nombre) && !/\(USADO/i.test(p.nombre);
}

/** "128GB" | "128 GB" | "1 TB" | "1TB" → gigas. */
function capacidadAGb(texto: string): number {
  const m = /(\d+)\s*(GB|TB)/i.exec(texto);
  if (!m) return 0;
  const n = Number(m[1]);
  return /TB/i.test(m[2] ?? "") ? n * 1024 : n;
}

export function etiquetaCapacidad(gb: number): string {
  if (gb <= 0) return "";
  return gb >= 1024 ? `${gb / 1024} TB` : `${gb} GB`;
}

/** Separa "IPHONE 17 PRO MAX 256 GB" en modelo, capacidad y lo que sobre. */
export function parsearIphone(nombre: string): {
  modelo: string;
  capacidadGb: number;
  detalle: string;
} {
  const sinMarca = nombre.replace(/^IPHONE\s*/i, "").trim();
  const m = /^(.*?)\s*(\d+\s*(?:GB|TB))\s*(.*)$/i.exec(sinMarca);
  if (!m) return { modelo: sinMarca, capacidadGb: 0, detalle: "" };
  return {
    modelo: (m[1] ?? "").trim(),
    capacidadGb: capacidadAGb(m[2] ?? ""),
    detalle: (m[3] ?? "").trim(),
  };
}

/** base < e < Plus < Pro < Pro Max, para ordenar adentro de una generación. */
function rangoModelo(modelo: string): number {
  const up = modelo.toUpperCase();
  if (up.includes("PRO MAX")) return 4;
  if (up.includes("PRO")) return 3;
  if (up.includes("PLUS")) return 2;
  if (/\bE\b/.test(up) || up.includes("SE")) return 1;
  return 0;
}

function generacion(modelo: string): number {
  const m = /(\d+)/.exec(modelo);
  return m ? Number(m[1]) : 0;
}

/**
 * El orden del lineup: 17 Pro Max, 17 Pro, 17, 16 Pro Max… y adentro de cada
 * modelo las capacidades de menor a mayor.
 *
 * Es lo único que la lista hereda de la grilla vieja, y es lo que impide que
 * el producto principal del negocio quede alfabético —con el "IPHONE 11"
 * arriba del "IPHONE 17 PRO"— que es exactamente al revés de como se pregunta.
 *
 * No filtra: ordena lo que le den.
 */
export function ordenarIphones(productos: ReadonlyArray<Producto>): Producto[] {
  return [...productos].sort((a, b) => {
    const ia = parsearIphone(a.nombre);
    const ib = parsearIphone(b.nombre);
    const ga = generacion(ia.modelo);
    const gb = generacion(ib.modelo);
    if (ga !== gb) return gb - ga;
    const ra = rangoModelo(ia.modelo);
    const rb = rangoModelo(ib.modelo);
    if (ra !== rb) return rb - ra;
    if (ia.capacidadGb !== ib.capacidadGb) return ia.capacidadGb - ib.capacidadGb;
    return a.nombre.localeCompare(b.nombre, "es");
  });
}

/**
 * Dos fichas del mismo modelo y la misma capacidad.
 *
 * Se compara por lo que el nombre DICE y no por el texto entero: "IPHONE 17
 * PRO 256 GB" y "IPHONE 17 PRO 256GB DEEP BLUE" son el mismo modelo aunque no
 * se parezcan como cadenas, y lo mismo pasa con "IPHONE 13 128 GB (USADO)"
 * contra "IPHONE 13 128GB (USADO)". Es lo que permite colgar cada unidad de su
 * modelo sin guardar un `padre_id` que habria que mantener a mano.
 */
export function mismoModelo(a: Producto, b: Producto): boolean {
  const pa = parsearIphone(a.nombre);
  const pb = parsearIphone(b.nombre);
  return (
    pa.capacidadGb === pb.capacidadGb &&
    pa.modelo.trim().toUpperCase() === pb.modelo.trim().toUpperCase()
  );
}

export interface PorModelo {
  /** Lo que se lista arriba: los modelos, mas las unidades que quedaron sueltas. */
  filas: Producto[];
  /** Las unidades de cada modelo, por id del modelo. */
  unidades: Map<number, Producto[]>;
}

/**
 * Cuelga cada unidad de su modelo.
 *
 * LA MISMA FORMA PARA SELLADOS Y PARA USADOS
 * ------------------------------------------
 * Las dos secciones tienen la misma estructura y la descubrieron por caminos
 * distintos: una fila que es EL MODELO —cuanto sale, en que colores viene, y
 * por eso en cero— y las fichas de los equipos concretos que hay. En sellados
 * el modelo es el que no tiene color; en usados, el precio de referencia. Lo
 * unico que cambia es el predicado, y por eso entra por parametro.
 *
 * Listar las dos cosas al mismo nivel mostraba cada modelo dos, tres o cuatro
 * veces con nombres casi iguales. Colgadas, el modelo es la entrada y adentro
 * esta lo que hay.
 *
 * UNA UNIDAD SIN MODELO NO SE ESCONDE. Si su modelo no esta en la lista —el
 * buscador lo dejo afuera, o nadie cargo esa fila— la unidad se queda arriba.
 * Antes que hacerla invisible, se muestra donde estaba.
 */
export function agruparEnModelos(
  productos: ReadonlyArray<Producto>,
  esModelo: (p: Producto) => boolean,
): PorModelo {
  const modelos = productos.filter(esModelo);
  const unidades = new Map<number, Producto[]>();
  const colgadas = new Set<number>();

  for (const u of productos) {
    if (esModelo(u)) continue;
    const modelo = modelos.find((m) => mismoModelo(m, u));
    if (!modelo) continue;
    unidades.set(modelo.id, [...(unidades.get(modelo.id) ?? []), u]);
    colgadas.add(u.id);
  }

  return { filas: productos.filter((p) => !colgadas.has(p.id)), unidades };
}

/**
 * Cuantas unidades hay de esa fila, contando las que cuelgan de ella.
 *
 * La ficha del modelo esta en cero y tiene que estarlo —es la lista de
 * precios—, pero lo que hay que leer recorriendo la lista es cuantos equipos
 * de ese modelo hay en el local. Sin esto, una fila con tres equipos adentro
 * dice 0 y el mostrador contesta que no hay.
 */
export function stockConUnidades(p: Producto, unidades: ReadonlyMap<number, Producto[]>): number {
  const propias = aNumero(p.stock_actual);
  const colgadas = (unidades.get(p.id) ?? []).reduce((t, u) => t + aNumero(u.stock_actual), 0);
  return propias + colgadas;
}

/** "17 PRO MAX" → "iPhone 17 Pro Max". Un Samsung se queda como esta. */
export function nombreDeModelo(modelo: string): string {
  const limpio = modelo.trim();
  if (!limpio) return "";
  if (/SAMSUNG|XIAOMI|MOTOROLA|GALAXY|REDMI|POCO/i.test(limpio)) return limpio;
  const bonito = limpio
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
    // "16E" se escribe "16e": la e es de la variante, no una inicial.
    .replace(/(\d)E\b/, "$1e");
  return `iPhone ${bonito}`;
}

export interface ModeloAgrupado {
  /** El modelo como lo devuelve `parsearIphone`: "17 PRO MAX". */
  clave: string;
  titulo: string;
  /** Las fichas del modelo, una por memoria, de menor a mayor. */
  memorias: Producto[];
}

/**
 * Junta las memorias de cada modelo en una sola fila.
 *
 * "iPhone 17 Pro Max" no existe en la base: existen el de 256, el de 512 y el
 * de 1 TB. Pero en el mostrador el modelo es UNA cosa y lo que cambia adentro
 * es cuanta memoria y de que color. Listarlo tres veces con el nombre casi
 * igual es la misma repeticion que ya se saco colgando las unidades de su
 * modelo: la lista se lee como la planilla de la que salio.
 *
 * El orden de los grupos es el del lineup —lo nuevo arriba— y adentro las
 * memorias de menor a mayor, que es como se pregunta.
 */
export function agruparPorModelo(productos: ReadonlyArray<Producto>): ModeloAgrupado[] {
  const grupos = new Map<string, Producto[]>();
  for (const p of ordenarIphones(productos)) {
    const clave = parsearIphone(p.nombre).modelo.trim().toUpperCase();
    grupos.set(clave, [...(grupos.get(clave) ?? []), p]);
  }
  return [...grupos.entries()].map(([clave, memorias]) => ({
    clave,
    titulo: nombreDeModelo(clave),
    memorias,
  }));
}
